import React from 'react';
import Modal from '@components/Modal';
import { useSpeakUpCompanyConfig } from '@hooks/useSpeakUpCompanyConfig';
import CloseIcon from '@mui/icons-material/Close';
import { sanitizeHtml } from '@utils/string.util';
import styles from './FreedomToSpeakUpChooserModal.module.scss';

export interface FreedomToSpeakUpChooserModalProps {
  open: boolean;
  onClose: () => void;
  onSelectShareFeelings: () => void;
  onSelectRaiseConcern: () => void;
}

/**
 * First step when Freedom to Speak Up is enabled: user picks classic wellbeing Speak Up vs raise a concern.
 */
export const FreedomToSpeakUpChooserModal: React.FC<FreedomToSpeakUpChooserModalProps> = ({
  open,
  onClose,
  onSelectShareFeelings,
  onSelectRaiseConcern,
}) => {
  const { buttonText, f2suPopUpDescription } = useSpeakUpCompanyConfig();

  const handleSetOpen = (next: boolean) => {
    if (!next) {
      onClose();
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Modal
      open={open}
      setOpen={handleSetOpen}
      overlay
      onTapBackgroundClose
      className={styles.root}
      aria-labelledby="f2su-chooser-title"
    >
      <div className={styles.headerRow}>
        <h2 id="f2su-chooser-title" className={styles.title}>
          {buttonText}
        </h2>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <CloseIcon aria-hidden />
        </button>
      </div>
      <p
        className={styles.subtitle}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(f2suPopUpDescription) }}
      />

      <div className={styles.grid}>
        <button
          type="button"
          className={styles.cardButton}
          onClick={onSelectShareFeelings}
          aria-label="Share how you're feeling: get wellbeing support and share thoughts with your admin team."
        >
          <span className={styles.cardTitle}>Share how you&apos;re feeling</span>
          <p className={styles.cardBody}>
            Get support with something affecting your wellbeing. Express your thoughts and feelings
            with your admin team.
          </p>
          <span className={styles.cardCta}>Share thoughts</span>
        </button>

        <button
          type="button"
          className={styles.cardButton}
          onClick={onSelectRaiseConcern}
          aria-label="Raise a concern: report a safety or conduct issue that needs addressing."
        >
          <span className={styles.cardTitle}>Raise a concern</span>
          <p className={styles.cardBody}>
            Report something that isn&apos;t right. This could be a safety issue, conduct problem,
            or something you&apos;ve witnessed that needs addressing.
          </p>
          <span className={styles.cardCta}>Raise a concern</span>
        </button>
      </div>

      <p className={styles.footer}>Both options allow you to remain anonymous</p>
    </Modal>
  );
};
